import type { JSX } from 'react';

import { Glyph, type IconProps } from './Glyph';

/**
 * The four destinations in the patient tab bar.
 *
 * Each one is drawn to be legible at 24px on a phone held at arm's length,
 * which is the only size it is ever used at. They are deliberately distinct in
 * silhouette rather than in detail: a tab is recognised by its outline long
 * before its strokes resolve, and many patients here are older.
 */

/** Booking: a calendar with a plus — "add a new appointment", not just "dates". */
export function CalendarPlusIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M20.5 11.5V7.5a3 3 0 0 0-3-3h-11a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h5" />
        <path d="M3.5 9.5h17M8 3v3M16 3v3" />
        <path d="M17.5 14v6M14.5 17h6" />
      </g>
    </Glyph>
  );
}

/** My appointments: a ticket — a thing you hold, not a thing you browse. */
export function TicketIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v1.5a2.5 2.5 0 0 0 0 5V16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.5a2.5 2.5 0 0 0 0-5z" />
        <path d="M13.5 6v1.5M13.5 11.25v1.5M13.5 16.5V18" />
      </g>
    </Glyph>
  );
}

/** Medical record: a folder — the familiar metaphor for a case file. */
export function FolderIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M3.5 7.5a2 2 0 0 1 2-2h3.2a2 2 0 0 1 1.5.7l1.1 1.3h7.2a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z" />
        <path d="M8 13h8" />
      </g>
    </Glyph>
  );
}

/** تنظیمات سایت: a map pin — where the مطب is, not when it works. */
export function MapPinIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M12 21s6.5-5.7 6.5-11a6.5 6.5 0 1 0-13 0c0 5.3 6.5 11 6.5 11z" />
        <circle cx="12" cy="10" r="2.5" />
      </g>
    </Glyph>
  );
}

/** Account: a person. */
export function UserIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <circle cx="12" cy="8.5" r="3.75" />
        <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
      </g>
    </Glyph>
  );
}

/** A chevron that points *forward* — the caller flips it for RTL, not the icon. */
export function ChevronIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />
    </Glyph>
  );
}

/**
 * The panel's own destination: a gauge, because a داشبورد answers "how full is
 * it?" before it answers anything else. Distinct in silhouette from the
 * calendar next to it in the same bar.
 */
export function GaugeIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M3.75 17.5a9 9 0 1 1 16.5 0" />
        <path d="m12 13.5 4-4" />
        <circle cx="12" cy="14.5" r="1.5" />
      </g>
    </Glyph>
  );
}

/** Several people: the کاربران section, as opposed to one account. */
export function UsersIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <circle cx="9.5" cy="8.5" r="3.5" />
        <path d="M3 19.5a6.5 6.5 0 0 1 13 0" />
        <path d="M16 5.5a3.5 3.5 0 0 1 0 6.75M17.5 14.5a6 6 0 0 1 3.5 5" />
      </g>
    </Glyph>
  );
}

/** Wallet: store credit, used on the account screen. */
/**
 * «پرداخت‌ها». A card rather than the wallet: اعتبار and پرداخت are two
 * different things on two different screens, and one glyph for both would make
 * the panel's own navigation lie about which is which.
 */
export function CreditCardIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <rect height="13" rx="2.5" width="18" x="3" y="5.5" />
        <path d="M3 10h18" />
        <path d="M6.5 14.5h3.5" />
      </g>
    </Glyph>
  );
}

export function WalletIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M3.5 8.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2" />
        <rect height="11" rx="2.5" width="17" x="3.5" y="8.5" />
        <path d="M20.5 12.5h-3.25a2 2 0 0 0 0 4h3.25" />
      </g>
    </Glyph>
  );
}

/**
 * «مدیریت سایت» — the public site: a globe, because it is the one screen in
 * the panel whose result is read by people outside the clinic.
 */
export function GlobeIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M3.5 12h17" />
        <path d="M12 3.5c2.5 2.4 3.75 5.2 3.75 8.5S14.5 18.1 12 20.5C9.5 18.1 8.25 15.3 8.25 12S9.5 5.9 12 3.5z" />
      </g>
    </Glyph>
  );
}

/** A telephone handset — the clinic's numbers, as tap-to-call links. */
export function PhoneIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <path d="M6.6 4.5h2.2l1.4 3.6-1.8 1.2a10.5 10.5 0 0 0 5.3 5.3l1.2-1.8 3.6 1.4v2.2a2 2 0 0 1-2 2A14.5 14.5 0 0 1 4.6 6.5a2 2 0 0 1 2-2z" />
    </Glyph>
  );
}

/** «گفتگو» — two speech bubbles: colleagues talking, not the clinic writing to a patient. */
export function ChatIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <path d="M14.5 9.5a5.5 5.5 0 0 1-8 4.9L3.5 15.5l1-3A5.5 5.5 0 1 1 14.5 9.5Z" />
        <path d="M9.5 16.5a5.5 5.5 0 0 0 8 1.9l3 1.1-1-3a5.5 5.5 0 0 0-2.2-7.4" />
      </g>
    </Glyph>
  );
}

/** «بیشتر» — four tiles: the rest of the panel, laid out like a home screen. */
export function GridIcon(props: IconProps): JSX.Element {
  return (
    <Glyph {...props}>
      <g>
        <rect height="6.5" rx="1.75" width="6.5" x="4" y="4" />
        <rect height="6.5" rx="1.75" width="6.5" x="13.5" y="4" />
        <rect height="6.5" rx="1.75" width="6.5" x="4" y="13.5" />
        <rect height="6.5" rx="1.75" width="6.5" x="13.5" y="13.5" />
      </g>
    </Glyph>
  );
}
